// BajaEstadistica.tsx

import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../security/AuthLogin";
import {
  fetchSoporteMensajes,
  sendSoporteMensaje,
  fetchArchivosSoporte,
} from "../api/soportesApi";
import { getAuthToken } from "../security/AuthLogin";
import { io, Socket } from "socket.io-client";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";
import "../css/favorito.css";
import ChatComponent from "../components/ChatComponent";
// ★ NUEVO: Importar el chat de n8n
/* import { createChat } from '@n8n/chat'; */
import '@n8n/chat/style.css';

// ---------------------
// INTERFACES
// ---------------------
interface BajaData {
  id_concurso: string;
  id_usuario: string;
  titulo: string;
  codigo_expediente: string;
  enlace: string | null;
  estado_concurso: string;
  estado_soporte_favorito: string | null;
  id_soporte: number;
  cliente_nombre?: string;
  cliente_email?: string;
  cliente_profilepic?: string;
  dueno_nombre?: string | null;
  dueno_email?: string | null;
  // Nuevo campo para la baja recomendada (almacenado en la columna "baja_presentada" de la tabla soportes)
  baja_presentada?: number;
}

interface MensajeSoporte {
  id_soporte_detalle: number;
  id_soporte: number;
  id_usuario: string;
  f_respuesta: string;
  hora_respuesta: string;
  texto: string;
  es_cliente: boolean;
}

interface ArchivoSoporte {
  path: string;
  uploaderRole: "cliente" | "admin" | "tecnico";
  dateFolder?: string | null;
}

interface TecnicoData {
  id_usuario: string;
  nombre: string;
  email: string;
}

const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;

// Mapeo del estado
const mapEstado = (estado: string | null): string => {
  if (!estado) return "En estudio";
  switch (estado.toLowerCase()) {
    case "en estudio":
      return "En estudio";
    case "valido":
      return "Viable";
    case "rechazado":
      return "No viable";
    case "oculto":
      return "Oculto";
    case "norecomendado":
      return "No recomendado";
    default:
      return estado.charAt(0).toUpperCase() + estado.slice(1);
  }
};

const BajaEstadistica: React.FC = () => {
  const { id_concurso, id_usuario } = useParams<{
    id_concurso: string;
    id_usuario: string;
  }>();
  const { userRole } = useAuth();

  const [loading, setLoading] = useState(false);
  const [baja, setBaja] = useState<BajaData | null>(null);
  const [, setMensajes] = useState<MensajeSoporte[]>([]);
  const [, setArchivos] = useState<ArchivoSoporte[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [, setAlguienTyping] = useState({
    isTyping: false,
    quien: "",
  });
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmType, setConfirmType] = useState<
    "tecnico" | "administrativo" | "juridico" | null
  >(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  // ★ NUEVO: Estado para controlar el chat de n8n
  /* const [n8nChatInitialized, setN8nChatInitialized] = useState(false); */

  const tipoConfig = {
    tecnico: {
      label: "Soporte técnico",
      endpoint: "/api/soporte-tecnico",
      redirectPath: (c: string, u: string) => `/soporte-tecnico/${c}/${u}`,
    },
    administrativo: {
      label: "Soporte administrativo",
      endpoint: "/api/soporte-administrativo",
      redirectPath: (c: string, u: string) =>
        `/soporte-administrativo/${c}/${u}`,
    },
    juridico: {
      label: "Soporte jurídico",
      endpoint: "/api/soporte-juridico",
      redirectPath: (c: string, u: string) => `/soporte-juridico/${c}/${u}`,
    },
  };

  /* // ★ NUEVO: Inicializar el chat de n8n solo para admins
  useEffect(() => {
    // Solo inicializar el chat si el usuario es admin y no se ha inicializado ya
    if (userRole === "admin" && !n8nChatInitialized) {
      // Crear el contenedor para el chat si no existe
      let chatContainer = document.getElementById("n8n-chat-container");
      if (!chatContainer) {
        chatContainer = document.createElement("div");
        chatContainer.id = "n8n-chat-container";
        document.body.appendChild(chatContainer);
      }

      // Configurar el chat de n8n
      createChat({
        webhookUrl:
          "https://abhogar.es:5678/webhook/6de34706-dedd-4982-b10a-94ab044fc37e/chat",
        target: "#n8n-chat-container",
        mode: "window",
        webhookConfig: {
          method: "POST",
          headers: {
            // Agregar las credenciales como headers de autorización básica
            Authorization: "Basic " + btoa("oclem:1010*"),
          },
        },
        initialMessages: [
          "¡Hola Admin! 👋",
          "Soy tu asistente virtual. ¿En qué puedo ayudarte hoy?",
        ],
        i18n: {
          en: {
            title: "Asistente Virtual - Admin",
            subtitle: "Chat con IA para administradores",
            footer: "",
            getStarted: "Nueva Conversación",
            inputPlaceholder: "Escribe tu consulta aquí...",
            closeButtonTooltip: "Cerrar chat",
          },
        },
        showWelcomeScreen: true,
        loadPreviousSession: true,
        enableStreaming: true,
        allowFileUploads: false,
      });

      setN8nChatInitialized(true);
    }

    // Cleanup al desmontar el componente o si el usuario no es admin
    return () => {
      if (userRole !== "admin") {
        const chatContainer = document.getElementById("n8n-chat-container");
        if (chatContainer) {
          chatContainer.remove();
        }
        setN8nChatInitialized(false);
      }
    };
  }, [userRole, n8nChatInitialized]); */

  // ★ NUEVO: Comprueba si ya existe un soporte
  const checkSupportExists = async (type: keyof typeof tipoConfig) => {
    if (!baja) return false;
    const token = getAuthToken();
    const resp = await fetch(
      `${BASE_URL}/api/soporte-existe?tipo=${type}&id_concurso=${baja.id_concurso}&id_usuario=${baja.id_usuario}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!resp.ok) return false;
    const json = await resp.json();
    return json.exists as boolean;
  };

  // ★ NUEVO: Wrapper para manejar clicks en Soporte técnico/Administrativo/Jurídico
  const handleButtonClick = async (type: keyof typeof tipoConfig) => {
    if (!baja) return;
    const exists = await checkSupportExists(type);
    if (exists) {
      await invokeSupportHandler(type, "");
    } else {
      setConfirmType(type);
      setConfirmMessage("");
      setConfirmModalVisible(true);
    }
  };

  // ★ NUEVO: Invoca el endpoint POST, envía mensaje inicial y redirige
  const invokeSupportHandler = async (
    type: keyof typeof tipoConfig,
    initialMessage: string
  ) => {
    if (!baja) return;
    const { endpoint, redirectPath } = tipoConfig[type];
    try {
      const token = getAuthToken();
      const resp = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id_concurso: baja.id_concurso,
          id_usuario: baja.id_usuario,
        }),
      });
      if (!resp.ok)
        throw new Error((await resp.text()) || "Error creando soporte");
      const data = await resp.json();
      if (data.success && data.id_soporte) {
        if (initialMessage.trim()) {
          await sendSoporteMensaje(
            data.id_soporte,
            DOMPurify.sanitize(initialMessage)
          );
        }
        window.location.href = redirectPath(baja.id_concurso, baja.id_usuario);
      } else {
        alert("No se pudo obtener el ID del soporte");
      }
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Error procesando la solicitud");
    }
  };

  // ★ NUEVO: Confirmación del modal
  const handleConfirmYes = async () => {
    if (confirmType) {
      await invokeSupportHandler(confirmType, confirmMessage);
      setConfirmModalVisible(false);
      setConfirmType(null);
      setConfirmMessage("");
    }
  };
  const handleConfirmNo = () => {
    setConfirmModalVisible(false);
    setConfirmType(null);
    setConfirmMessage("");
  };

  const [tecnicosActivos, setTecnicosActivos] = useState<TecnicoData[]>([]);
  const [selectedTecnico, setSelectedTecnico] = useState("");
  const [showReassignModal, setShowReassignModal] = useState(false);

  // Estado para controlar el input de Baja recomendada
  const [bajaRecomendada, setBajaRecomendada] = useState("");

  // Socket
  useEffect(() => {
    const s = io(BASE_URL, {
      withCredentials: true,
      auth: (cb) => cb({ token: localStorage.getItem("authToken") }),
    });
    setSocket(s);
    return () => {
      s.disconnect();
    };
  }, []);

  // Carga inicial
  useEffect(() => {
    if (id_concurso && id_usuario) {
      loadBaja(id_concurso, id_usuario);
    }
  }, [id_concurso, id_usuario]);

  // Obtener info de baja
  const loadBaja = async (concursoId: string, userIdParam: string) => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${BASE_URL}/api/bajas-estadisticas/detalle/${concursoId}/${userIdParam}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        const msg = await response.text();
        throw new Error(msg || "Error al cargar baja estadística");
      }
      const data = await response.json();
      if (data.success) {
        setBaja(data.baja);
        // Actualizamos el input con el valor actual (si existe)
        if (
          data.baja.baja_presentada !== undefined &&
          data.baja.baja_presentada !== null
        ) {
          setBajaRecomendada(data.baja.baja_presentada.toString());
        } else {
          setBajaRecomendada("");
        }
        if (data.baja.id_soporte) {
          await loadChat(data.baja.id_soporte);
          await loadArchivos(data.baja.id_soporte);
        }
      }
    } catch (error: any) {
      console.error("Error loading baja:", error);
    } finally {
      setLoading(false);
    }
  };

  // Cargar el chat
  const loadChat = async (idSoporte: number) => {
    try {
      const resp = await fetchSoporteMensajes(idSoporte);
      if (resp.success) {
        setMensajes(resp.messages);
        scrollToBottom();
      }
    } catch (error: any) {
      console.error("Error loading chat:", error);
    }
  };

  // Cargar archivos
  const loadArchivos = async (idSoporte: number) => {
    try {
      const resp = await fetchArchivosSoporte(idSoporte);
      if (resp.success) {
        setArchivos(resp.archivos);
      }
    } catch (error: any) {
      console.error("Error loading files:", error);
    }
  };

  // Socket: unir sala soporte
  useEffect(() => {
    if (socket && baja?.id_soporte) {
      socket.emit("joinSoporte", baja.id_soporte);
      socket.on("nuevoMensaje", (msg: MensajeSoporte) => {
        setMensajes((prev) => [...prev, msg]);
        scrollToBottom();
      });
      socket.on("alguienEstaEscribiendo", ({ isTyping, quien }) => {
        setAlguienTyping({ isTyping, quien });
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        if (isTyping) {
          typingTimeoutRef.current = setTimeout(() => {
            setAlguienTyping({ isTyping: false, quien: "" });
          }, 5000);
        }
      });
    }
    return () => {
      if (socket) {
        socket.off("nuevoMensaje");
        socket.off("alguienEstaEscribiendo");
      }
    };
  }, [socket, baja?.id_soporte]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Si es admin, cargamos lista de técnicos
  useEffect(() => {
    if (userRole === "admin") {
      fetchTecnicos();
    }
  }, [userRole]);

  // Obtener técnicos
  const fetchTecnicos = async () => {
    try {
      const token = getAuthToken();
      const resp = await fetch(`${BASE_URL}/api/usuarios/tecnicos-activos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const json = await resp.json();
        if (json.success) {
          setTecnicosActivos(json.tecnicos);
        }
      }
    } catch (error) {
      console.error("Error fetching technicians:", error);
    }
  };

  // Dueño
  const handleDueñoAction = async (
    action: "becomeOwner" | "dropOwner" | "assignTo",
    newDueno?: string
  ) => {
    if (!baja?.id_soporte) return;
    try {
      const resp = await fetch(
        `${BASE_URL}/api/soportes/${baja.id_soporte}/dueno`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ action, ...(newDueno && { newDueno }) }),
        }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Error al actualizar dueño: ${txt}`);
      }
      const data = await resp.json();
      if (data.success) {
        await loadBaja(baja.id_concurso, baja.id_usuario);
        alert(data.message);
      } else {
        alert(data.message || "Error al cambiar dueño");
      }
    } catch (error: any) {
      console.error("Error updating owner:", error);
      alert(`Error al cambiar dueño: ${error.message}`);
    }
  };

  const handleReassignModalOpen = () => {
    setShowReassignModal(true);
  };

  const handleReassignConfirm = async () => {
    if (!selectedTecnico) {
      alert("Selecciona un técnico");
      return;
    }
    await handleDueñoAction("assignTo", selectedTecnico);
    setShowReassignModal(false);
  };

  if (loading) {
    return (
      <div className="concurso-container">
        <p>Cargando...</p>
      </div>
    );
  }

  if (!baja) {
    return (
      <div className="concurso-container">
        <p>Baja no encontrada</p>
      </div>
    );
  }

  // Determinar si hay responsable, etc.
  const hayDueño = !!baja.dueno_nombre;
  const soyDueño = false; // Ajusta si es necesario comparar IDs
  const esAdmin = userRole === "admin";
  const esTec = userRole === "tecnico";
  const esAdminOTec = esAdmin || esTec;

  return (
    <div className="favorito-page">
      <aside className="sticky-sidebar">
        {/* Ver favorito (sólo link) */}
        <button className="sidebar-button">
          <Link
            to={`/favorito/${baja?.id_concurso}/${baja?.id_usuario}`}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            Ver favorito
          </Link>
        </button>
        {/* Ver concurso (sólo link) */}
        <button className="sidebar-button">
          <Link
            to={`/concurso/${baja?.id_concurso}`}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            Ver concurso
          </Link>
        </button>
        {/* Soportes con confirmación */}
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("tecnico")}
        >
          Soporte técnico
        </button>
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("administrativo")}
        >
          Soporte administrativo
        </button>
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("juridico")}
        >
          Soporte jurídico
        </button>
      </aside>

      <div className="concurso-container">
        <div
          className="concurso-header"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div>
            <h1>Baja Estadística</h1>
            <p>Expediente: {baja.codigo_expediente}</p>
            {esAdminOTec &&
              (hayDueño ? (
                <p>
                  Responsable: {baja.dueno_nombre} ({baja.dueno_email})
                </p>
              ) : (
                <p>Sin responsable asignado</p>
              ))}
          </div>
          {esAdminOTec && (
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {!hayDueño && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleDueñoAction("becomeOwner")}
                >
                  Asignarme
                </button>
              )}
              {hayDueño && (soyDueño || esAdmin) && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleDueñoAction("dropOwner")}
                >
                  Liberar
                </button>
              )}
              {esAdmin && (
                <button
                  className="btn btn-secondary"
                  onClick={handleReassignModalOpen}
                >
                  Reasignar
                </button>
              )}
            </div>
          )}
        </div>

        {showReassignModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Reasignar soporte</h3>
              <select
                value={selectedTecnico}
                onChange={(e) => setSelectedTecnico(e.target.value)}
              >
                <option value="">Seleccionar técnico...</option>
                {tecnicosActivos.map((t) => (
                  <option key={t.id_usuario} value={t.id_usuario}>
                    {t.nombre} ({t.email})
                  </option>
                ))}
              </select>
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={handleReassignConfirm}
                  style={{ marginRight: "1rem" }}
                >
                  Confirmar
                </button>
                <button onClick={() => setShowReassignModal(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="section">
          <h2>Detalles</h2>
          <div className="info-grid">
            <div className="field">
              <label>Título</label>
              <span>{baja.titulo}</span>
            </div>
            <div className="enlaces-container">
              <label>Enlace</label>
              {baja.enlace ? (
                <a href={baja.enlace} className="enlace-oficial" target="_blank" rel="noopener noreferrer">
                  Enlace oficial
                </a>
              ) : (
                <span>No disponible</span>
              )}
            </div>
          </div>
          <div className="estado-container" style={{ marginTop: "1rem" }}>
            <label>
              <strong>Estado del soporte:</strong>
            </label>{" "}
            <span>{mapEstado(baja.estado_soporte_favorito)}</span>
          </div>
          {/* Bloque para Baja recomendada */}
          {userRole !== "cliente" ? (
            <div
              className="baja-recomendada-container"
              style={{
                marginTop: "1rem",
                display: "flex",
                alignItems: "center",
              }}
            >
              <label htmlFor="baja-recomendada-input">
                <strong>Baja recomendada:</strong>
              </label>
              <input
                type="number"
                id="baja-recomendada-input"
                value={bajaRecomendada}
                onChange={(e) => setBajaRecomendada(e.target.value)}
                placeholder="0 - 100"
                style={{ marginLeft: "0.5rem", width: "80px" }}
              />
              <button
                onClick={async () => {
                  const num = parseFloat(bajaRecomendada);
                  if (isNaN(num) || num < 0 || num > 100) {
                    alert("Por favor, ingresa un número válido entre 0 y 100.");
                    return;
                  }
                  try {
                    const token = getAuthToken();
                    if (!token) throw new Error("No autenticado");
                    const response = await fetch(
                      `${BASE_URL}/api/bajas-estadisticas/baja-recomendada/${baja.id_concurso}/${baja.id_usuario}`,
                      {
                        method: "PATCH",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ baja_recomendada: num }),
                      }
                    );
                    if (!response.ok) {
                      const errorText = await response.text();
                      throw new Error(
                        errorText || "Error al actualizar baja recomendada"
                      );
                    }
                    const data = await response.json();
                    if (data.success) {
                      alert("Baja recomendada actualizada correctamente.");
                      setBaja((prev) =>
                        prev ? { ...prev, baja_presentada: num } : prev
                      );
                    } else {
                      alert(
                        data.message || "Error al actualizar baja recomendada."
                      );
                    }
                  } catch (error: any) {
                    console.error("Error updating baja recomendada:", error);
                    alert(
                      "Error al actualizar baja recomendada: " + error.message
                    );
                  }
                }}
                style={{ marginLeft: "0.5rem" }}
              >
                Guardar
              </button>
            </div>
          ) : (
            <div style={{ marginTop: "1rem" }}>
              <strong>Baja recomendada:</strong>{" "}
              <span>
                {bajaRecomendada !== "" ? `${bajaRecomendada}%` : "Sin asignar"}
              </span>
            </div>
          )}
        </div>

        <div className="section">
          <h2>Comunicación</h2>
          <ChatComponent
            idSoporte={baja.id_soporte}
            clienteNombre={baja.cliente_nombre}
            clienteEmail={baja.cliente_email}
            clienteProfilePic={baja.cliente_profilepic}
            sendText={userRole === "admin" || userRole === "tecnico"} // 🔥 Solo staff puede escribir
            allowFileUpload={true}
            showTypingIndicator={userRole === "admin" || userRole === "tecnico"} // 🔥 Solo staff ve typing
          />
        </div>
      </div>
      {/* ★ NUEVO: modal de confirmación */}
      {confirmModalVisible && confirmType && (
        <div className="modal-overlay-soportes">
          <div className="modal-content-soportes">
            <h3>
              ¿Quieres solicitar el{" "}
              <strong>{tipoConfig[confirmType].label}</strong>?
            </h3>
            <textarea
              value={confirmMessage}
              onChange={(e) => setConfirmMessage(e.target.value)}
              placeholder="Describe tu solicitud…"
              rows={4}
              style={{ width: "100%", margin: "1rem 0" }}
            />
            <div className="modal-buttons-soportes">
              <button
                onClick={handleConfirmYes}
                disabled={!confirmMessage.trim()}
              >
                Sí
              </button>
              <button onClick={handleConfirmNo}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BajaEstadistica;
